import { useRef } from "react";
import { Camera, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 4 * 1024 * 1024;

/**
 * Reusable mobile-first photo picker.
 * - "Fotoğraf Çek" → opens device camera (capture="environment")
 * - "Galeriden Seç" → file picker
 * - Shows preview + remove button when a file is selected.
 *
 * Props:
 *  - file: File | null
 *  - preview: data url | null
 *  - onChange(file|null, dataUrl|null)
 *  - testIdPrefix: string (default "photo-picker")
 *  - compact: boolean (smaller buttons, used inside other modals)
 */
export function PhotoPicker({ file, preview, onChange, testIdPrefix = "photo-picker", compact = false, cameraLabel = "Fotoğraf Çek", galleryLabel = "Galeriden Seç" }) {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const handleFile = (f) => {
    if (!f) {
      onChange(null, null);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Resim 4MB'dan büyük olamaz");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => onChange(f, e.target.result);
    reader.readAsDataURL(f);
  };

  const padding = compact ? "py-2.5" : "py-3";
  const iconSize = compact ? 16 : 18;

  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          data-testid={`${testIdPrefix}-camera-button`}
          onClick={() => cameraRef.current?.click()}
          className={`flex items-center justify-center gap-2 bg-zinc-950 text-white px-3 ${padding} text-sm font-bold uppercase tracking-wider border-b-4 border-black active:border-b-0 active:translate-y-[3px] transition-all`}
        >
          <Camera size={iconSize} strokeWidth={2.5} />
          {cameraLabel}
        </button>
        <button
          type="button"
          data-testid={`${testIdPrefix}-gallery-button`}
          onClick={() => galleryRef.current?.click()}
          className={`flex items-center justify-center gap-2 bg-white text-zinc-950 border-2 border-zinc-950 px-3 ${padding} text-sm font-bold uppercase tracking-wider hover:bg-zinc-50 transition-colors`}
        >
          <ImageIcon size={iconSize} strokeWidth={2.5} />
          {galleryLabel}
        </button>
      </div>

      <input
        data-testid={`${testIdPrefix}-camera-input`}
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0] || null)}
      />
      <input
        data-testid={`${testIdPrefix}-gallery-input`}
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0] || null)}
      />

      {file && (
        <div className="mt-3 flex items-start gap-3" data-testid={`${testIdPrefix}-selected`}>
          {preview && (
            <img
              src={preview}
              alt="Önizleme"
              data-testid={`${testIdPrefix}-preview`}
              className="w-24 h-24 object-cover border-2 border-zinc-200 shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-zinc-950 truncate">{file.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">
              {(file.size / 1024).toFixed(0)} KB
            </div>
            <button
              type="button"
              data-testid={`${testIdPrefix}-clear-button`}
              onClick={() => handleFile(null)}
              className="mt-2 inline-flex items-center gap-1 px-2 py-1 border border-zinc-300 text-zinc-600 text-[11px] font-bold uppercase tracking-wider hover:border-red-600 hover:text-red-600 transition-colors"
            >
              <X size={12} strokeWidth={2.5} />
              Kaldır
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
